import React from 'react';
import { FileText, Code, Image as ImageIcon, File, FileCode } from 'lucide-react';
import { isXmlFile } from '../XmlViewer';
import { DRAFTS_CATEGORY, DRAFT_TAG, DRAFT_ATTACHMENT_TAG } from '../drafts/draftModel';

/**
 * The shared vocabulary for artifacts — icons, provenance, tags, categories and
 * the content peek. Mirrors components/tasks/taskMeta.js: everything here is
 * pure and module-scope so a file described on a card, in a row, in a shelf
 * column and in the open panel is described identically.
 *
 * This used to live as a `getIcon` closure inside Files.js, recreated on every
 * render and passed down to every card — which defeated any memoization the
 * cards could have had.
 */

// Tags the app writes for its own bookkeeping. They carry meaning, but as
// badges and filters — not as user-visible `#chips` you can remove.
export const SYSTEM_TAGS = new Set(['ai-generated', 'ai-chat', DRAFT_TAG, DRAFT_ATTACHMENT_TAG]);

export const CORE_CATEGORIES = [
    { id: 'Docs', label: 'Docs' },
    { id: 'Mockups', label: 'Mockups' },
    { id: 'Assets', label: 'Assets' },
    // Real category, but the control bar filters it out of every view unless you
    // ask for it. A scratchpad you wrote once and never reopened is noise in the
    // artifact grid — and the whole point of the dock is that you stop looking
    // at it.
    { id: DRAFTS_CATEGORY, label: 'Drafts' },
];

/** Sentinel column for files whose category isn't in any known list. */
export const UNCATEGORIZED = '__uncategorized__';

const ICON_TINT = {
    doc: 'text-primary',
    mockup: 'text-accent',
    asset: 'text-purple-400',
    code: 'text-accent',
};

export function getFileIcon(type, fileName, className = 'h-8 w-8') {
    // Extension wins over `type`: an .xml stored as a plain doc is still XML.
    if (isXmlFile(fileName)) return <FileCode className={`${className} text-amber-400`} />;
    switch (type) {
        case 'doc': return <FileText className={`${className} ${ICON_TINT.doc}`} />;
        case 'mockup': return <Code className={`${className} ${ICON_TINT.mockup}`} />;
        case 'code': return <Code className={`${className} ${ICON_TINT.code}`} />;
        case 'asset': return <ImageIcon className={`${className} ${ICON_TINT.asset}`} />;
        default: return <File className={`${className} text-muted-foreground`} />;
    }
}

/** 'advisor' when the agent authored it, otherwise 'you'. */
export function fileSource(file) {
    return (file.tags || []).includes('ai-generated') ? 'advisor' : 'you';
}

/** A file you dropped into the chat composer — noise in the artifact list. */
export function isChatUpload(file) {
    return (file.tags || []).includes('ai-chat');
}

/** A scratchpad note. Hidden from the artifact views behind a toggle. */
export function isDraft(file) {
    return file.category === DRAFTS_CATEGORY || (file.tags || []).includes(DRAFT_TAG);
}

/**
 * An image pasted into a draft. Never an artifact in its own right — it exists
 * only to be pointed at by a `forge-asset://` reference — so unlike drafts there
 * is no toggle that reveals these.
 */
export function isDraftAttachment(file) {
    return (file.tags || []).includes(DRAFT_ATTACHMENT_TAG);
}

/** Tags minus the ones the app owns. */
export function visibleTags(file) {
    const tags = file.tags;
    if (!tags || tags.length === 0) return EMPTY_TAGS;
    return tags.filter((t) => !SYSTEM_TAGS.has(t));
}
const EMPTY_TAGS = [];

/** Every tag across the project that a human would want to filter on. */
export function collectTags(files) {
    const seen = new Set();
    for (const f of files) {
        for (const t of f.tags || []) {
            if (!SYSTEM_TAGS.has(t)) seen.add(t);
        }
    }
    return [...seen].sort();
}

/** Core categories plus whatever the project has defined. */
export function categoriesFor(project) {
    const custom = project?.custom_categories || [];
    if (custom.length === 0) return CORE_CATEGORIES;
    return [...CORE_CATEGORIES, ...custom.map((c) => ({ id: c.id, label: c.label || c.id }))];
}

export function isImageFile(file) {
    return file.type === 'asset' && !!file.content?.startsWith('data:image');
}

/** Base64 blobs and the like — nothing to read, nothing to search. */
function isBinary(content) {
    return !content || content.startsWith('data:');
}

const MINUTE = 60000;
const UNITS = [
    [MINUTE * 60 * 24 * 365, 'y'],
    [MINUTE * 60 * 24 * 30, 'mo'],
    [MINUTE * 60 * 24 * 7, 'w'],
    [MINUTE * 60 * 24, 'd'],
    [MINUTE * 60, 'h'],
    [MINUTE, 'm'],
];

/**
 * "3d", "2h", "now" — the shelf's columns are ~240px wide and date-fns'
 * `formatDistanceToNow` spends most of that on the words "about" and "ago".
 * The list view keeps the long form, where there's room for it.
 */
export function shortAgo(value) {
    if (!value) return '';
    const delta = Date.now() - new Date(value).getTime();
    if (delta < MINUTE) return 'now';
    for (const [ms, suffix] of UNITS) {
        if (delta >= ms) return `${Math.floor(delta / ms)}${suffix}`;
    }
    return 'now';
}

// ─── Content derivations ─────────────────────────────────────────────────────
// Both of these are keyed by file id and re-derive only when `content` changes
// identity. That matters because useUpdateFile merges optimistically, so `files`
// gets a fresh array on every autosave keystroke — without this cache, every
// card on screen would re-slice and re-lowercase its whole body each time.

const PEEK_CACHE = new Map();
const LOWER_CACHE = new Map();

const NEWLINE = /\r?\n/;
const PEEK_LINES = 10;
// Enough to fill the peek box at any card width without walking a 200KB body.
const PEEK_CHARS = 700;

/**
 * The first few lines of a document, for the card thumbnail. Returns null when
 * there is nothing to show, in which case the caller falls back to an icon.
 */
export function getPeek(file) {
    if (isBinary(file.content)) return null;

    const cached = PEEK_CACHE.get(file.id);
    if (cached && cached.src === file.content) return cached.peek;

    const peek = file.content
        .slice(0, PEEK_CHARS)
        .split(NEWLINE)
        // Drop leading blank lines only — interior ones are part of the shape.
        .reduce((acc, line) => (acc.length === 0 && !line.trim() ? acc : (acc.push(line), acc)), [])
        .slice(0, PEEK_LINES)
        .join('\n')
        .trimEnd();

    PEEK_CACHE.set(file.id, { src: file.content, peek: peek || null });
    return peek || null;
}

/** Lowercased body for content search. Empty string for anything unsearchable. */
export function lowerContent(file) {
    if (isBinary(file.content)) return '';

    const cached = LOWER_CACHE.get(file.id);
    if (cached && cached.src === file.content) return cached.lower;

    const lower = file.content.toLowerCase();
    LOWER_CACHE.set(file.id, { src: file.content, lower });
    return lower;
}

/**
 * The line a search matched, split around the hit so the card can highlight it.
 * Returns null when the query only matched the filename — the card keeps its
 * normal peek in that case, because the name is already right there.
 */
export function getMatchSnippet(file, query) {
    if (!query || isBinary(file.content)) return null;

    const at = lowerContent(file).indexOf(query);
    if (at === -1) return null;

    const start = file.content.lastIndexOf('\n', at) + 1;
    const end = file.content.indexOf('\n', at);
    const line = file.content.slice(start, end === -1 ? undefined : end);
    const offset = at - start;

    // Keep the hit in view on a long line, with a leading ellipsis for context.
    const lead = Math.max(0, offset - 40);
    const clipped = line.slice(lead, lead + 160);
    const hitAt = offset - lead;

    return {
        before: (lead > 0 ? '…' : '') + clipped.slice(0, hitAt),
        match: clipped.slice(hitAt, hitAt + query.length),
        after: clipped.slice(hitAt + query.length),
    };
}
